import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const LearningDashboard = () => {
  const { user } = useAuth();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchLearningData();
  }, []);

const fetchLearningData = async () => {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      setLoading(false);
      return;
    }

    const res = await fetch('/api/education/learning-dashboard', {
      headers: { 'Authorization': `Bearer ${token}` }
    });

    if (res.ok) {
      const data = await res.json();
      // Filter out "other" courses
      const filteredCourses = data.courses.filter(c => 
        c.name !== 'other' && 
        c.name !== 'uncategorized' && 
        c.name !== 'null'
      );
      setCourses(filteredCourses);
    }
  } catch (error) {
    console.error('Error fetching learning data:', error);
  } finally {
    setLoading(false);
  }
};

  if (loading) {
    return <div className="text-center py-4">Loading courses...</div>;
  }

  if (courses.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        <p className="text-4xl mb-2">📚</p>
        <p>You haven't started any courses yet.</p>
        <Link to="/learn" className="text-blue-500 hover:underline mt-2 inline-block">
          Browse Courses →
        </Link>
      </div>
    );
  }

  // Separate completed and in-progress courses
  const completedCourses = courses.filter(c => c.completed);
  const inProgressCourses = courses.filter(c => !c.completed && c.completedModules > 0);
  const notStartedCourses = courses.filter(c => !c.completed && c.completedModules === 0);

  return (
    <div className="space-y-4">
      {/* Overall Stats */}
      <div className="grid grid-cols-3 gap-4 mb-4">
        <div className="bg-green-50 rounded-lg p-3 text-center">
          <p className="text-sm text-gray-600">Completed</p>
          <p className="text-2xl font-bold text-green-600">{completedCourses.length}</p>
        </div>
        <div className="bg-blue-50 rounded-lg p-3 text-center">
          <p className="text-sm text-gray-600">In Progress</p>
          <p className="text-2xl font-bold text-blue-600">{inProgressCourses.length}</p>
        </div>
        <div className="bg-purple-50 rounded-lg p-3 text-center">
          <p className="text-sm text-gray-600">Average Score</p>
          <p className="text-2xl font-bold text-purple-600">
            {courses.length > 0 
              ? Math.round(courses.reduce((sum, c) => sum + c.score, 0) / courses.length) 
              : 0}%
          </p>
        </div>
      </div>

      {/* Completed Courses */}
      {completedCourses.length > 0 && (
        <div>
          <h3 className="font-semibold text-gray-700 mb-2">✅ Completed</h3>
          <div className="space-y-3">
            {completedCourses.map((course, idx) => (
              <div key={idx} className="border rounded-lg p-4">
                <div className="flex justify-between items-start">
                  <div>
                    <div className="font-medium">{course.name}</div>
                    <div className="text-sm text-gray-500">
                      Score: <span className="font-semibold text-green-600">{course.score}%</span>
                      {course.bonusCredits > 0 && (
                        <span className="ml-2 text-yellow-600">🏆 +{course.bonusCredits} bonus credits</span>
                      )}
                    </div>
                    <div className="text-sm text-gray-500">
                      {course.completedModules}/{course.totalModules} modules • 
                      {course.creditsEarned > 0 && (
                        <span className="ml-1 text-green-600">💰 {course.creditsEarned} credits earned</span>
                      )}
                    </div>
                  </div>
                  <Link
                    to={`/learn/${course.slug}`}
                    className="text-blue-500 hover:text-blue-700 text-sm"
                  >
                    Review →
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* In Progress Courses with Dots */}
      {inProgressCourses.length > 0 && (
        <div>
          <h3 className="font-semibold text-gray-700 mb-2">🔄 In Progress</h3>
          <div className="space-y-3">
            {inProgressCourses.map((course, idx) => (
              <div key={idx} className="border rounded-lg p-4">
                <div className="flex justify-between items-start">
                  <div>
                    <div className="font-medium">{course.name}</div>
                    <div className="text-sm text-gray-500">
                      Score: <span className="font-semibold">{course.score}%</span>
                    </div>
                    <div className="text-sm text-gray-500">
                      {course.completedModules}/{course.totalModules} modules complete
                    </div>
                    <div className="flex gap-1 mt-2">
                      {Array.from({ length: course.totalModules }, (_, i) => (
                        <div
                          key={i}
                          className={`w-3 h-3 rounded-full ${
                            i < course.completedModules
                              ? 'bg-green-500'
                              : 'bg-gray-300'
                          }`}
                          title={i < course.completedModules ? 'Completed' : 'Not started'}
                        />
                      ))}
                    </div>
                    <div className="text-xs text-gray-400 mt-1">
                      {course.totalCredits > 0 && (
                        <span>💰 {course.totalCredits} credits available on completion</span>
                      )}
                    </div>
                  </div>
                  {course.nextModule && (
                    <Link
                      to={`/learn/${course.nextModule}`}
                      className="text-sm bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700"
                    >
                      Continue →
                    </Link>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Not Started Courses */}
      {notStartedCourses.length > 0 && (
        <div>
          <h3 className="font-semibold text-gray-700 mb-2">📖 Not Started</h3>
          <div className="space-y-3">
            {notStartedCourses.map((course, idx) => (
              <div key={idx} className="border rounded-lg p-4">
                <div className="flex justify-between items-start">
                  <div>
                    <div className="font-medium">{course.name}</div>
                    <div className="text-sm text-gray-500">
                      {course.totalModules} modules • {course.totalCredits} credits available
                    </div>
                  </div>
                  <Link
                    to={`/learn/${course.slug}`}
                    className="text-sm bg-green-600 text-white px-3 py-1 rounded hover:bg-green-700"
                  >
                    Start →
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default LearningDashboard;
